import { useRef, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AuthApi } from "../services/api/AuthApi";
import { Auth } from "../models/Auth";
import useAuth from "../hooks/useAuth";
import { STORELINK_FORGOT_PASSWORD_PATH } from "../services/api/StoreLinks";
import { AxiosError } from "axios";
import {
  ErrorResponse,
  ProblemDetail,
  ReportHubError,
} from "../services/config/AxiosConfig";
import { navigateTo } from "../utils/NavigationHelper";
import LoadingComponent from "./LoadingComponent";
import ReportHubLogo from "./logo/ReportHubLogo";

const Login = () => {
  const { login } = useAuth();
  const location = useLocation();
  const userRef = useRef<HTMLInputElement>(null);

  const navigate = useNavigate();
  const from: string = location.state?.referringPathName || "/reports/dashboard";

  const [user, setUser] = useState("");
  const [pwd, setPwd] = useState("");
  const [isInvalidLogin, setIsInvalidLogin] = useState<boolean | undefined>();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setFocus();
  }, [isInvalidLogin]);

  const setFocus = () => {
    if (userRef.current) userRef.current?.focus();
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    AuthApi.login(user, pwd)
      .then((jwtToken: Auth) => {
        const accessToken = jwtToken.access_token;
        login(accessToken);
        setUser("");
        setPwd("");
        setIsInvalidLogin(false);
        navigate(from, { replace: true });
      })
      .catch((error: AxiosError) => {
        const errorResponse: ErrorResponse = error?.response
          ?.data as ErrorResponse;
        if (
          error.response?.status === 400 &&
          (errorResponse.error === "login_access_denied" ||
            errorResponse.error === "invalid_grant")
        ) {
          setIsInvalidLogin(true);
          setUser("");
          setPwd("");
          setFocus();
        } else {
          const reportHubError: ReportHubError = {
            message: error.message,
            responseData: error?.response?.data as ProblemDetail,
          };
          navigateTo("/error", {
            state: {
              error: reportHubError,
            },
            replace: false,
          });
        }
      })
      .finally(() => setIsLoading(false));
  };

  if (isLoading) return <LoadingComponent />;

  return (
    <section>
      <div className="auth-form-container">
        <form className="auth-form" onSubmit={handleSubmit}>
          {isInvalidLogin && (
            <div className="invalid-login">
              Invalid login credentials. Please try again.
            </div>
          )}
          <div className="auth-form-content">
            <div className="auth-form-logo">
              <ReportHubLogo variant="static" />
            </div>
            <h3 className="auth-form-title">Log In</h3>
            <div className="form-group mt-3">
              <label htmlFor="username">Username:</label>
              <input
                className="form-control mt-1"
                type="text"
                id="username"
                ref={userRef}
                autoComplete="off"
                onChange={(e) => {
                  setUser(e.target.value);
                  setIsInvalidLogin(false);
                }}
                value={user}
                required
              />
            </div>
            <div className="form-group mt-3">
              <label htmlFor="password">Password:</label>
              <input
                className="form-control mt-1"
                type="password"
                id="password"
                onChange={(e) => {
                  setPwd(e.target.value);
                  setIsInvalidLogin(false);
                }}
                value={pwd}
                required
              />
            </div>
            <div className="d-grid gap-2 mt-3">
              <button type="submit" className="btn btn-primary">
                Login
              </button>
            </div>
            <p className="forgot-password text-right mt-4 text-center">
              Forgot <a href={STORELINK_FORGOT_PASSWORD_PATH}>password?</a>
            </p>
          </div>
        </form>
      </div>
    </section>
  );
};

export default Login;
